import {
  Badge,
  Card,
  DataList,
  Flex,
  Grid,
  Heading,
  Text,
} from '@radix-ui/themes';
import { CommonTooltip } from 'components/common/tooltip';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/common';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { t } from 'i18next';
import { getMachineActiveModelID, getModelKey } from 'lib_ts/classes/ms.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';

export const MachineFirmwareSection = () => {
  const machine = useMachinesPortalStore((s) => s.machine);
  const models = useMachinesPortalStore((s) => s.models);

  if (!machine) {
    return null;
  }

  const activeKey = getModelKey(machine);
  const activeID = getMachineActiveModelID(machine);
  const activeModel = models.find((m) => m._id === activeID);

  const tooltipContent =
    activeModel &&
    [
      `**${t('common.name')}**: ${activeModel.name}`,
      `**${t('common.key')}**: ${activeKey}`,
      `**${t('admin.id')}**: \`${activeID}\``,
    ].join('\n\n');

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
      <Heading size="4">{t('admin.model-and-firmware')}</Heading>
      <Card size="2">
        <Grid
          columns={{
            xs: '2',
            lg: '3',
          }}
          gap={RADIX.FLEX.GAP.MD}
        >
          {/* Ball Type */}
          <DataList.Root orientation="vertical">
            <DataList.Item>
              <DataList.Label color={RADIX.COLOR.SECONDARY}>
                {t('admin.active-ball-type')}
              </DataList.Label>
              <DataList.Value>
                <Flex direction="column" gap={RADIX.FLEX.GAP.XS} align="start">
                  <Badge>{machine.ball_type}</Badge>

                  {machine.last_ball_type_changed && (
                    <Text size={RADIX.TEXT.SIZE.XS}>
                      {t('common.updated')}:{' '}
                      {formatDate(machine.last_ball_type_changed)}
                    </Text>
                  )}
                </Flex>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>

          {/* Model Status */}
          <DataList.Root orientation="vertical">
            <DataList.Item>
              <DataList.Label color={RADIX.COLOR.SECONDARY}>
                {t('admin.active-model')}
              </DataList.Label>
              <DataList.Value>
                <Flex
                  direction="column"
                  gap={RADIX.FLEX.GAP.XS}
                  align="start"
                  maxWidth="100%"
                >
                  {activeModel ? (
                    <>
                      <CommonTooltip
                        trigger={
                          <Badge
                            style={{
                              maxWidth: '100%',
                            }}
                          >
                            <Text
                              truncate
                              style={{
                                // match Badge sizing
                                lineHeight: 'var(--line-height-1)',
                                letterSpacing: 'var(--letter-spacing-1)',
                              }}
                            >
                              {activeModel.name}
                            </Text>
                          </Badge>
                        }
                        text_md={tooltipContent}
                      />
                      <Text size={RADIX.TEXT.SIZE.XS}>
                        {t('common.updated')}:{' '}
                        {formatDate(activeModel._changed)}
                      </Text>
                    </>
                  ) : (
                    <Badge color={RADIX.COLOR.DANGER}>{t('admin.none')}</Badge>
                  )}
                </Flex>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>

          {/* Firmware Status */}
          <DataList.Root orientation="vertical">
            <DataList.Item>
              <DataList.Label color={RADIX.COLOR.SECONDARY}>
                {t('admin.firmware-version')}
              </DataList.Label>
              <DataList.Value>
                <Flex
                  direction="column"
                  gap={RADIX.FLEX.GAP.XS}
                  align="start"
                  maxWidth="100%"
                >
                  {machine.last_firmware ? (
                    <Badge
                      style={{
                        maxWidth: '100%',
                      }}
                    >
                      <Text
                        truncate
                        style={{
                          // match Badge sizing
                          lineHeight: 'var(--line-height-1)',
                          letterSpacing: 'var(--letter-spacing-1)',
                        }}
                      >
                        {machine.last_firmware}
                      </Text>
                    </Badge>
                  ) : (
                    'N/A'
                  )}
                  {machine.last_firmware_updated && (
                    <Text size={RADIX.TEXT.SIZE.XS}>
                      {t('common.updated')}:{' '}
                      {formatDate(machine.last_firmware_updated)}
                    </Text>
                  )}
                </Flex>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
        </Grid>
      </Card>
    </Flex>
  );
};
